import { useEffect, useState, useCallback } from "react";
import { api, money } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { useAuth, isWriter } from "@/context/AuthContext";
import { PageHeader, Section, Metric, SourceLink, EmptyState } from "@/components/primitives";
import { Modal, Field, inputCls, PrimaryButton, GhostButton } from "@/components/Modal";
import { StatusBadge } from "@/components/StatusBadge";
import { AudienceSelector } from "@/components/AudienceSelector";
import { toast } from "sonner";

export default function Finance() {
  const { currentId } = useProjects();
  const { user } = useAuth();
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/projects/${currentId}/finance`);
      setFin(data);
    } finally {
      setLoading(false);
    }
  }, [currentId]);

  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setForm({
      bank_verified_cash: fin?.bank_verified_cash?.amount ?? "",
      commitments: fin?.commitments?.amount ?? "",
      current_spending: fin?.current_spending?.amount ?? "",
      cash_remaining: fin?.cash_remaining?.amount ?? "",
      contingency_used: fin?.contingency?.used ?? "",
      notes: fin?.notes ?? "",
      audiences: fin?.audiences || ["internal"],
    });
    setEdit(true);
  };

  const save = async () => {
    const num = (v) => (v === "" || v === null ? null : Number(v));
    try {
      await api.put(`/api/projects/${currentId}/finance`, {
        bank_verified_cash: { amount: num(form.bank_verified_cash), note: num(form.bank_verified_cash) === null ? "Not verified" : "Bank-verified" },
        commitments: { amount: num(form.commitments), note: num(form.commitments) === null ? "Not verified" : "Committed" },
        current_spending: { amount: num(form.current_spending), note: num(form.current_spending) === null ? "Not verified" : "Recorded" },
        cash_remaining: { amount: num(form.cash_remaining), note: num(form.cash_remaining) === null ? "Not verified" : "Verified" },
        contingency: { ...(fin?.contingency || {}), used: form.contingency_used === "" ? null : Number(form.contingency_used) },
        notes: form.notes,
        audiences: form.audiences,
      });
      toast.success("Finance updated");
      setEdit(false);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  if (!currentId || loading) return <div className="cm-label animate-pulse">Loading…</div>;
  if (fin?.restricted) {
    return (
      <div>
        <PageHeader eyebrow="Finance" title="Finance" />
        <EmptyState title="Restricted" description={fin.message} testid="finance-restricted" />
      </div>
    );
  }
  if (!fin) return <EmptyState title="No finance data" />;

  return (
    <div data-testid="finance-page">
      <PageHeader
        eyebrow="Finance"
        title="Funding & Cost Reconciliation"
        description={`Source: ${fin.summary_source}. Closeout review ${fin.closeout_review_date}. Funding, budget, recorded actuals, commitments, estimates and bank-verified cash are kept separate. Unknown current metrics read "Not verified" — never $0.`}
      >
        {isWriter(user) && <PrimaryButton data-testid="finance-edit-btn" onClick={openEdit}>Edit verification</PrimaryButton>}
      </PageHeader>

      <Section title="Funding (contracted & wired)">
        <div className="grid grid-cols-1 gap-px bg-[#2C2C2A] sm:grid-cols-3">
          <Metric label="Total contracted" value={money(fin.funding?.total_contracted)} />
          {fin.funding?.wires?.map((w, i) => (
            <Metric key={i} label={w.label} value={money(w.amount)} note={`Wired ${w.date}`} />
          ))}
        </div>
      </Section>

      <Section title="Budget versions (keep dates & versions separate)">
        <div className="overflow-x-auto border border-[#2C2C2A]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#2C2C2A] text-left">
                <th className="cm-label p-3">Version</th>
                <th className="cm-label p-3">Amount</th>
                <th className="cm-label p-3">Date</th>
                <th className="cm-label p-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {fin.budget_versions?.map((b, i) => (
                <tr key={i} className="border-b border-[#2C2C2A] last:border-0">
                  <td className="p-3 text-[#F9F9F8]">{b.label}</td>
                  <td className="p-3 font-display text-[#F9F9F8]">{money(b.amount)}</td>
                  <td className="p-3 text-[#A1A19D]">{b.date}</td>
                  <td className="p-3 text-[#A1A19D]">{b.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Recorded actuals & estimates">
        <div className="grid grid-cols-1 gap-px bg-[#2C2C2A] sm:grid-cols-2 lg:grid-cols-4">
          <Metric label={`Recorded actuals (${fin.recorded_actuals?.as_of})`} value={money(fin.recorded_actuals?.amount)} note={fin.recorded_actuals?.note} />
          <Metric label="Estimated future costs" value={money(fin.estimates?.estimated_future_costs?.amount)} note={`As of ${fin.estimates?.estimated_future_costs?.as_of}`} />
          <Metric label="Estimated final cost" value={money(fin.estimates?.estimated_final_cost?.amount)} note="An estimate is NOT spent" />
          <Metric label="Commitments" value={fin.commitments?.amount ?? null} note={fin.commitments?.note} />
        </div>
      </Section>

      <Section title="Verification — not yet determinable">
        <div className="grid grid-cols-1 gap-px bg-[#2C2C2A] sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="Bank-verified cash" value={fin.bank_verified_cash?.amount ?? null} note={fin.bank_verified_cash?.note} highlight />
          <Metric label="Current spending" value={fin.current_spending?.amount ?? null} note={fin.current_spending?.note} highlight />
          <Metric label="Cash remaining" value={fin.cash_remaining?.amount ?? null} note={fin.cash_remaining?.note} highlight />
          <Metric
            label="Contingency ($5,000 projected)"
            value={fin.contingency?.used ?? null}
            note={fin.contingency?.note}
            highlight
          />
        </div>
      </Section>

      <Section title="Composer allocation">
        <div className="border border-[#2C2C2A] bg-[#1A1A18] p-6">
          <div className="flex flex-wrap items-center gap-6">
            <div><div className="cm-label">Projected</div><div className="font-display text-2xl">{money(fin.composer?.projected)}</div></div>
            <div><div className="cm-label">Actual ({fin.composer?.as_of})</div><div className="font-display text-2xl">{money(fin.composer?.actual)}</div></div>
            <div className="flex-1"><p className="text-sm text-[#A1A19D]">{fin.composer?.note}</p></div>
          </div>
        </div>
      </Section>

      <Section title="Source documents">
        <ul className="space-y-2">
          {fin.source_links?.map((s, i) => (
            <li key={i} className="flex items-center justify-between border border-[#2C2C2A] bg-[#1A1A18] px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#F9F9F8]">{s.label}</span>
                <span className="cm-label">{s.date}</span>
                {s.verified && <StatusBadge status="Verified" />}
              </div>
              <SourceLink url={s.url} label="Open in Drive" testid={`finance-source-${i}`} />
            </li>
          ))}
        </ul>
        {fin.notes && (
          <div className="mt-4 border border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.08)] p-4 text-sm text-[#F9F9F8]" data-testid="finance-notes">
            <div className="cm-label text-[#F59E0B] mb-1">Reconciliation note</div>
            {fin.notes}
          </div>
        )}
      </Section>

      <Modal open={edit} onClose={() => setEdit(false)} title="Edit verified figures" testid="finance-edit-modal">
        <p className="mb-4 text-xs text-[#A1A19D]">Leave a field blank to keep it as "Not verified". Enter a number only when it is bank-verified / confirmed.</p>
        <Field label="Bank-verified cash ($)"><input className={inputCls} data-testid="fin-bank" value={form.bank_verified_cash} onChange={(e) => setForm({ ...form, bank_verified_cash: e.target.value })} /></Field>
        <Field label="Commitments ($)"><input className={inputCls} value={form.commitments} onChange={(e) => setForm({ ...form, commitments: e.target.value })} /></Field>
        <Field label="Current spending ($)"><input className={inputCls} value={form.current_spending} onChange={(e) => setForm({ ...form, current_spending: e.target.value })} /></Field>
        <Field label="Cash remaining ($)"><input className={inputCls} value={form.cash_remaining} onChange={(e) => setForm({ ...form, cash_remaining: e.target.value })} /></Field>
        <Field label="Contingency used ($)"><input className={inputCls} value={form.contingency_used} onChange={(e) => setForm({ ...form, contingency_used: e.target.value })} /></Field>
        <Field label="Reconciliation note"><textarea className={inputCls} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        <div className="mb-4"><AudienceSelector label="Who can view this financial summary" value={form.audiences} onChange={(a) => setForm({ ...form, audiences: a })} /></div>
        <div className="flex justify-end gap-2">
          <GhostButton onClick={() => setEdit(false)}>Cancel</GhostButton>
          <PrimaryButton data-testid="fin-save" onClick={save}>Save</PrimaryButton>
        </div>
      </Modal>
    </div>
  );
}
